import { supabase } from '../lib/supabase';
import { startOfMonth, endOfMonth, subMonths, format } from 'date-fns';
import { OrganizationService } from './organization.service';

const PAGE_SIZE = 1000;

/** Paginate through all rows from a Supabase table */
async function fetchAll(table: string, orgId: string, filters?: Record<string, any>, columns = '*'): Promise<any[]> {
  let all: any[] = [];
  let offset = 0;
  let hasMore = true;

  while (hasMore) {
    let query = supabase
      .from(table)
      .select(columns)
      .eq('organization_id', orgId)
      .range(offset, offset + PAGE_SIZE - 1);

    if (filters) {
      for (const [key, val] of Object.entries(filters)) {
        if (key.startsWith('gte_')) query = query.gte(key.slice(4), val);
        else if (key.startsWith('lte_')) query = query.lte(key.slice(4), val);
        else if (key.startsWith('in_')) query = query.in(key.slice(3), val);
        else if (key.startsWith('not_')) query = query.not(key.slice(4), 'is', val);
        else query = query.eq(key, val);
      }
    }

    const { data, error } = await query;
    if (error) throw error;
    if (!data || data.length === 0) break;
    all = all.concat(data);
    hasMore = data.length === PAGE_SIZE;
    offset += PAGE_SIZE;
  }

  return all;
}

export interface SalesMetrics {
  totalRevenue: number;
  totalSales: number;
  averageSalePrice: number;
  topSalesperson?: {
    name: string;
    sales: number;
    revenue: number;
  };
}

export interface InventoryMetrics {
  totalVehicles: number;
  availableVehicles: number;
  soldVehicles: number;
  onHoldVehicles: number;
  byMake: Array<{ make: string; count: number }>;
  byPriceRange: Array<{ range: string; count: number }>;
  averageDaysInStock: number;
  inventoryTurnoverRate: number;
  agingBuckets: Array<{ range: string; count: number; percentage: number }>;
  totalInventoryValue: number;
  averageInventoryValue: number;
}

export interface CustomerMetrics {
  totalCustomers: number;
  newCustomersThisMonth: number;
  repeatCustomers: number;
  averageCustomerValue: number;
  topLeadSources: Array<{ source: string; count: number; revenue: number }>;
  leadConversionRate: number;
  leadsByStatus: Array<{ status: string; count: number }>;
  conversionFunnel: Array<{ stage: string; count: number; percentage: number }>;
}

export interface MonthlyTrend {
  month: string;
  sales: number;
  revenue: number;
}

export class ReportsService {
  static async getSalesMetrics(dateRange: { start: Date; end: Date }): Promise<SalesMetrics> {
    try {
      const orgId = await OrganizationService.getOrganizationId();

      const { data: deals, error } = await supabase
        .from('deals')
        .select(`
          *,
          salesperson:sales_rep_id(
            id, email,
            profiles(first_name, last_name)
          )
        `)
        .eq('organization_id', orgId)
        .gte('actual_close_date', dateRange.start.toISOString())
        .lte('actual_close_date', dateRange.end.toISOString())
        .not('actual_close_date', 'is', null)
        .in('stage', ['won', 'completed']);

      if (error) throw error;

      const totalRevenue = deals?.reduce((sum, deal) => sum + (deal.total_amount || deal.final_price || deal.vehicle_price || 0), 0) || 0;
      const totalSales = deals?.length || 0;
      const averageSalePrice = totalSales > 0 ? totalRevenue / totalSales : 0;

      // Group by salesperson
      const salesBySalesperson = deals?.reduce((acc, deal) => {
        const salespersonId = deal.sales_rep_id;
        if (!acc[salespersonId]) {
          acc[salespersonId] = {
            name: deal.salesperson?.profiles?.first_name + ' ' + deal.salesperson?.profiles?.last_name || deal.salesperson?.email || 'Unknown',
            sales: 0,
            revenue: 0
          };
        }
        acc[salespersonId].sales += 1;
        acc[salespersonId].revenue += deal.total_amount || 0;
        return acc;
      }, {} as Record<string, any>);

      const topSalesperson = Object.values(salesBySalesperson || {})
        .sort((a: any, b: any) => b.revenue - a.revenue)[0] as {
          name: string;
          sales: number;
          revenue: number;
        } | undefined;

      return {
        totalRevenue,
        totalSales,
        averageSalePrice,
        topSalesperson
      };
    } catch (error) {
      console.error('Error fetching sales metrics:', error);
      return {
        totalRevenue: 0,
        totalSales: 0,
        averageSalePrice: 0
      };
    }
  }

  static async getInventoryMetrics(): Promise<InventoryMetrics> {
    try {
      const orgId = await OrganizationService.getOrganizationId();
      const vehicles = await fetchAll('vehicles', orgId);

      const statusCounts = vehicles?.reduce((acc, vehicle) => {
        acc[vehicle.status] = (acc[vehicle.status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>) || {};

      // Group by make
      const byMake = vehicles?.reduce((acc, vehicle) => {
        const existing = acc.find((item: { make: string; count: number }) => item.make === vehicle.make);
        if (existing) {
          existing.count += 1;
        } else {
          acc.push({ make: vehicle.make, count: 1 });
        }
        return acc;
      }, [] as Array<{ make: string; count: number }>) || [];

      // Group by price range
      const priceRanges = [
        { range: 'Under $25k', min: 0, max: 25000 },
        { range: '$25k-$50k', min: 25000, max: 50000 },
        { range: '$50k-$75k', min: 50000, max: 75000 },
        { range: '$75k-$100k', min: 75000, max: 100000 },
        { range: 'Over $100k', min: 100000, max: Infinity }
      ];

      const byPriceRange = priceRanges.map(range => ({
        range: range.range,
        count: vehicles?.filter(v => (v.sale_price || v.list_price || 0) >= range.min && (v.sale_price || v.list_price || 0) < range.max).length || 0
      }));

      // Calculate average days in stock for available vehicles
      const availableVehicles = vehicles?.filter(v => v.status === 'available') || [];
      const totalDaysInStock = availableVehicles.reduce((sum, vehicle) => {
        const daysInStock = Math.floor((new Date().getTime() - new Date(vehicle.created_at).getTime()) / (1000 * 60 * 60 * 24));
        return sum + daysInStock;
      }, 0);
      const averageDaysInStock = availableVehicles.length > 0 ? totalDaysInStock / availableVehicles.length : 0;

      // Calculate inventory aging buckets
      const agingRanges = [
        { range: '0-30 days', min: 0, max: 30 },
        { range: '31-60 days', min: 31, max: 60 },
        { range: '61-90 days', min: 61, max: 90 },
        { range: '90+ days', min: 91, max: Infinity }
      ];

      const agingBuckets = agingRanges.map(range => {
        const count = availableVehicles.filter(v => {
          const daysInStock = Math.floor((new Date().getTime() - new Date(v.created_at).getTime()) / (1000 * 60 * 60 * 24));
          return daysInStock >= range.min && daysInStock < range.max;
        }).length;
        return {
          range: range.range,
          count,
          percentage: availableVehicles.length > 0 ? Math.round((count / availableVehicles.length) * 100) : 0
        };
      });

      // Calculate inventory turnover rate (annualized)
      const soldLastYear = vehicles?.filter(v => {
        if (!v.sold_at) return false;
        const soldDate = new Date(v.sold_at);
        const yearAgo = new Date();
        yearAgo.setFullYear(yearAgo.getFullYear() - 1);
        return soldDate >= yearAgo;
      }).length || 0;
      
      // Average inventory = (current available + sold last year) / 2 — proper denominator
      const averageInventory = ((statusCounts.available || 0) + soldLastYear) / 2;
      const inventoryTurnoverRate = averageInventory > 0 ? Number((soldLastYear / averageInventory).toFixed(2)) : 0;

      // Calculate inventory value with proper COALESCE fallback chain
      const totalInventoryValue = availableVehicles.reduce((sum, v) => sum + (v.sale_price || v.list_price || v.cost_price || 0), 0);
      const averageInventoryValue = availableVehicles.length > 0 ? totalInventoryValue / availableVehicles.length : 0;

      return {
        totalVehicles: vehicles?.length || 0,
        availableVehicles: statusCounts.available || 0,
        soldVehicles: statusCounts.sold || 0,
        onHoldVehicles: (statusCounts.reserved || 0) + (statusCounts.in_transit || 0) + (statusCounts.in_service || 0),
        byMake: byMake.sort((a: { make: string; count: number }, b: { make: string; count: number }) => b.count - a.count).slice(0, 5),
        byPriceRange,
        averageDaysInStock: Math.round(averageDaysInStock),
        inventoryTurnoverRate,
        agingBuckets,
        totalInventoryValue,
        averageInventoryValue
      };
    } catch (error) {
      console.error('Error fetching inventory metrics:', error);
      return {
        totalVehicles: 0,
        availableVehicles: 0,
        soldVehicles: 0,
        onHoldVehicles: 0,
        byMake: [],
        byPriceRange: [],
        averageDaysInStock: 0,
        inventoryTurnoverRate: 0,
        agingBuckets: [],
        totalInventoryValue: 0,
        averageInventoryValue: 0
      };
    }
  }

  static async getCustomerMetrics(dateRange: { start: Date; end: Date }): Promise<CustomerMetrics> {
    try {
      const orgId = await OrganizationService.getOrganizationId();

      // Get all customers (paginated)
      const customers = await fetchAll('customers', orgId);

      // Get new customers in date range
      const newCustomers = customers.filter(c =>
        new Date(c.created_at) >= dateRange.start && new Date(c.created_at) <= dateRange.end
      );

      // Get completed deals (paginated)
      const deals = await fetchAll('deals', orgId, { in_stage: ['won', 'completed'] }, 'customer_id, total_amount, final_price, vehicle_price, stage');

      // Calculate repeat customers
      const customerPurchaseCounts = deals?.reduce((acc, deal) => {
        acc[deal.customer_id] = (acc[deal.customer_id] || 0) + 1;
        return acc;
      }, {} as Record<string, number>) || {};

      const repeatCustomers = Object.values(customerPurchaseCounts).filter(count => count > 1).length;

      // Calculate average customer value
      const totalRevenue = deals?.reduce((sum, deal) => sum + (deal.total_amount || 0), 0) || 0;
      const uniqueCustomers = Object.keys(customerPurchaseCounts).length;
      const averageCustomerValue = uniqueCustomers > 0 ? totalRevenue / uniqueCustomers : 0;

      // Get lead sources with revenue
      const leadSourceData = customers?.reduce((acc, customer) => {
        const source = customer.lead_source || 'Unknown';
        if (!acc[source]) {
          acc[source] = { source, count: 0, revenue: 0 };
        }
        acc[source].count += 1;
        
        // Add revenue from this customer's deals
        const customerRevenue = deals?.filter(d => d.customer_id === customer.id)
          .reduce((sum, deal) => sum + (deal.total_amount || 0), 0) || 0;
        acc[source].revenue += customerRevenue;
        
        return acc;
      }, {} as Record<string, { source: string; count: number; revenue: number }>) || {};

      const topLeadSources = Object.values(leadSourceData)
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 5);

      // Calculate lead conversion rate
      const convertedLeads = customers?.filter(c => 
        c.lead_status === 'won' || deals?.some(d => d.customer_id === c.id)
      ).length || 0;
      const leadConversionRate = customers?.length > 0 
        ? Math.round((convertedLeads / customers.length) * 100) 
        : 0;

      // Get leads by status - USING ACTUAL SCHEMA VALUES
      const leadsByStatus = ['new', 'contacted', 'test_drive', 'proposal', 'won', 'lost', 'inactive']
        .map(status => ({
          status,
          count: customers?.filter(c => c.lead_status === status).length || 0
        }));

      // Calculate conversion funnel - USING ACTUAL SCHEMA VALUES
      const funnelStages = [
        { stage: 'Total Leads', filter: () => true },
        { stage: 'Contacted', filter: (c: any) => ['contacted', 'test_drive', 'proposal', 'won'].includes(c.lead_status) },
        { stage: 'Test Drive', filter: (c: any) => ['test_drive', 'proposal', 'won'].includes(c.lead_status) },
        { stage: 'Proposal', filter: (c: any) => ['proposal', 'won'].includes(c.lead_status) },
        { stage: 'Won', filter: (c: any) => c.lead_status === 'won' }
      ];

      const totalLeads = customers?.length || 0;
      const conversionFunnel = funnelStages.map(stage => {
        const count = customers?.filter(stage.filter).length || 0;
        return {
          stage: stage.stage,
          count,
          percentage: totalLeads > 0 ? Math.round((count / totalLeads) * 100) : 0
        };
      });

      return {
        totalCustomers: customers?.length || 0,
        newCustomersThisMonth: newCustomers?.length || 0,
        repeatCustomers,
        averageCustomerValue,
        topLeadSources,
        leadConversionRate,
        leadsByStatus,
        conversionFunnel
      };
    } catch (error) {
      console.error('Error fetching customer metrics:', error);
      return {
        totalCustomers: 0,
        newCustomersThisMonth: 0,
        repeatCustomers: 0,
        averageCustomerValue: 0,
        topLeadSources: [],
        leadConversionRate: 0,
        leadsByStatus: [],
        conversionFunnel: []
      };
    }
  }

  static async getMonthlyTrends(months: number = 6): Promise<MonthlyTrend[]> {
    try {
      const orgId = await OrganizationService.getOrganizationId();
      const trends: MonthlyTrend[] = [];

      for (let i = months - 1; i >= 0; i--) {
        const monthStart = startOfMonth(subMonths(new Date(), i));
        const monthEnd = endOfMonth(monthStart);

        const { data: deals, error } = await supabase
          .from('deals')
          .select('total_amount')
          .eq('organization_id', orgId)
          .gte('actual_close_date', monthStart.toISOString())
          .lte('actual_close_date', monthEnd.toISOString())
          .not('actual_close_date', 'is', null)
          .in('stage', ['won', 'completed']);

        if (error) throw error;

        trends.push({
          month: format(monthStart, 'MMM'),
          sales: deals?.length || 0,
          revenue: deals?.reduce((sum, deal) => sum + (deal.total_amount || 0), 0) || 0
        });
      }

      return trends;
    } catch (error) {
      console.error('Error fetching monthly trends:', error);
      return [];
    }
  }
}